import { Suspense, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import LeadFormModalWithWrapper from "@Components/LeadFormModal";
import wrapperHOC from "@Utils/wrapperHOC";
import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import FullPageLoader from "@Components/FullPageLoader";
import getReferrerDetails from "@Pages/Home/helpers/GetReferralDetailsHelper";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";

interface ReferrerDetails {
  name: string;
  code: string;
}

interface ReferredByCardProps {
  isMobile?: boolean;
}

// Lazy load the content components
const DesktopContent = lazy(() => import("./ReferredContent/ReferredContentDesktop"));
const MobileContent = lazy(() => import("./ReferredContent/ReferredContentMobile"));

const ReferredByCard: React.FC<ReferredByCardProps> = ({ isMobile = false }) => {
  const location = useLocation();
  const { isBelow } = useResponsiveness();
  const isBelowMD = isBelow(SCREEN_SIZE.MD, false);
  const [referrerDetails, setReferrerDetails] = useState<ReferrerDetails | null>(null);
  const [hasError, setHasError] = useState(false);
  const isLFOneFilled = useSelector(({ leadFormModal }: AppState) => leadFormModal.isLFOneFilled);
  const isModalOpen = useSelector(({ leadFormModal }: AppState) => leadFormModal.openLeadFormModal);

  useEffect(() => {
    if (isLFOneFilled && !isModalOpen && referrerDetails) {
      setReferrerDetails(null);
    }
  }, [isLFOneFilled, isModalOpen]);

  useEffect(() => {
    const loadReferrerDetails = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("refer_code");
      const isLeadFormFilledForReferral = getParsedLocalStorageData("refer-details") as {
        referCode?: string;
      } | null;
      const isLeadFormSubmitted = localStorage.getItem("lead-form-submitted") === "true";

      setReferrerDetails(null);
      setHasError(false);

      if (code && !(isLeadFormFilledForReferral?.referCode === code && isLeadFormSubmitted)) {
        try {
          const details = await getReferrerDetails(code);
          if (!details || !details.name) {
            setHasError(true);
            return;
          }
          const name = details.name.toLowerCase() === "student" ? "Your Buddy" : details.name;
          setReferrerDetails({ name, code });
        } catch (error) {
          console.error("Failed to load referrer details:", error);
          setHasError(true);
        }
      }
    };

    loadReferrerDetails();
  }, [location.search]);

  if (hasError || !referrerDetails) {
    return null;
  }

  const Content = isMobile || isBelowMD ? MobileContent : DesktopContent;
  const loaderHeight = isMobile || isBelowMD ? "350px" : "320px";

  return (
    <>
      <Suspense fallback={<FullPageLoader height={loaderHeight} />}>
        <Content referrerDetails={referrerDetails} />
      </Suspense>
      <LeadFormModalWithWrapper />
    </>
  );
};

export default wrapperHOC(ReferredByCard, {
  componentName: "ReferredByCard-Home",
  showForChina: true,
});
